import { CareHome } from '../types';

// Get URL parameter for pin style
export const getPinStyle = (): 'round' | 'balloon' => {
    const urlParams = new URLSearchParams(window.location.search);
    const pinStyle = urlParams.get('pinstyle');
    return pinStyle === 'balloon' ? 'balloon' : 'round';
};

// Get marker color based on freshness_days as specified in design
export const getMarkerColor = (freshness_days: number): string => {
    if (freshness_days >= 1460) return '#606163';
    if (freshness_days >= 1095) return '#614041';
    if (freshness_days >= 730) return '#791616';
    if (freshness_days >= 365) return '#8dac8e';
    if (freshness_days >= 180) return '#5fb761';
    return '#00ba1d';
};

// Get openscore color based on score - matching OpenScoreCard exactly
export const getOpenScoreColor = (score: number): { color1: string; color2: string } => {
    if (score > 8) return { color1: "#27E757", color2: "#0DAF35" };
    if (score >= 6.5) return { color1: "#86DF11", color2: "#5CAE13" };
    if (score >= 4.5) return { color1: "#F5C60A", color2: "#DA840B" };
    if (score >= 2) return { color1: "#F7774A", color2: "#DF520C" };
    return { color1: "#F5443A", color2: "#C11626" };
};

// Legacy function - keeping for compatibility with balloon style
export const getGlyphColor = (score: number): string => {
    if (score > 8) return '#27E757'; // Green for high scores
    if (score >= 6.5) return '#86DF11'; // Orange for medium scores
    if (score >= 4.5) return '#F5C60A'; // Red for low scores
    if (score >= 2) return '#F7774A'; // Red for low scores
    return '#F5443A'; // Gray for very low scores
};

// Create a round pin with openscore coloring (NEW DEFAULT STYLE)
export const createRoundPin = (_freshness_days: number, score: number, _is_premium: boolean = false): HTMLElement => {
    // Create a wrapper div that can be scaled
    const wrapper = document.createElement('div');
    wrapper.style.display = 'inline-block';
    wrapper.style.transformOrigin = 'center center';
    
    // Create a round SVG pin with white border and openscore coloring
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const size =  32 ;
    svg.setAttribute('width', size.toString());
    svg.setAttribute('height', size.toString());
    svg.setAttribute('viewBox', `0 0 ${size} ${size}`);

    // Create gradient definition - matching OpenScoreCard
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', `gradient-${Math.random().toString(36).substr(2, 9)}`);
    gradient.setAttribute('gradientTransform', 'rotate(43.09)'); // Match OpenScoreCard

    const scoreColors = getOpenScoreColor(score);
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '25.83%'); // Match OpenScoreCard gradient
    stop1.setAttribute('stop-color', scoreColors.color1);
    
    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '57.78%'); // Match OpenScoreCard gradient
    stop2.setAttribute('stop-color', scoreColors.color2);

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs.appendChild(gradient);
    svg.appendChild(defs);

    // Main circle with gradient fill
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    const radius = (size / 2) - 2; // Account for border
    circle.setAttribute('cx', (size / 2).toString());
    circle.setAttribute('cy', (size / 2).toString());
    circle.setAttribute('r', radius.toString());
    circle.setAttribute('fill', `url(#${gradient.getAttribute('id')})`);
    circle.setAttribute('stroke', 'white');
    circle.setAttribute('stroke-width', '3');
    circle.setAttribute('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))');

    // Score text as integer
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', (size / 2).toString());
    text.setAttribute('y', (size / 2 + 1).toString()); // Slight adjustment for centering
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('fill', 'white');
    text.setAttribute('font-size', '12');
    text.setAttribute('font-weight', 'bold');
    text.setAttribute('font-family', 'Arial, sans-serif');
    text.setAttribute('text-shadow', '1px 1px 2px rgba(0,0,0,0.5)');
    text.textContent = score.toFixed(1).toString();

    svg.appendChild(circle);
    svg.appendChild(text);
    
    // Add SVG to wrapper
    wrapper.appendChild(svg);

    return wrapper;
};

// Create a balloon pin (LEGACY STYLE for pinstyle=balloon)
export const createBalloonPin = (freshness_days: number, score: number, is_premium: boolean = false): HTMLElement => {
    // Create a custom pin element using the original balloon style
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '11');
    circle.setAttribute('fill', getGlyphColor(score));
    circle.setAttribute('stroke', 'white');
    circle.setAttribute('stroke-width', '2');

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '12');
    text.setAttribute('y', '16');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('fill', 'white');
    text.setAttribute('font-size', '12');
    text.setAttribute('font-weight', 'bold');
    text.textContent = score.toFixed(1).toString();

    svg.appendChild(circle);
    svg.appendChild(text);

    const color = getMarkerColor(freshness_days);
    // Use 130% scale (1.3) for premium care homes, 110% (1.1) for regular ones
    const scale = is_premium ? 1.3 : 1.1;
    
    const pinElement = new google.maps.marker.PinElement({
        background: color,
        glyph: svg,
        scale: scale    
    });
    return pinElement.element;
};

// Main pin creation function that chooses style based on URL parameter
export const createPinElement = (freshness_days: number, score: number, is_premium: boolean = false): HTMLElement => {
    const pinStyle = getPinStyle();
    
    if (pinStyle === 'balloon') {
        return createBalloonPin(freshness_days, score, is_premium);
    } else {
        // Default to round pin style
        return createRoundPin(freshness_days, score, is_premium);
    }
};


// Create custom marker SVG with score and color (legacy function - keeping for compatibility)
export const createCustomMarkerIcon = (careHome: CareHome): string => {
    const color = getMarkerColor(careHome.freshness_days);
    const size = careHome.is_premium ? 65 : 50; // 130% larger for premium
    const fontSize = careHome.is_premium ? 14 : 11;
    
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <circle cx="25" cy="25" r="22" fill="${color}" stroke="#ffffff" stroke-width="2"/>
            <text x="25" y="25" text-anchor="middle" dominant-baseline="central" 
                  fill="white" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">
                ${careHome.open_score.toFixed(1)}
            </text>
        </svg>
    `)}`;
};

// Format freshness days into human readable text
export const formatFreshness = (days: number): string => {
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
};

// Get freshness category for display
export const getFreshnessCategory = (days: number): string => {
    if (days < 180) return 'Very Fresh';
    if (days < 365) return 'Fresh';
    if (days < 730) return 'Moderate';
    if (days < 1095) return 'Old';
    if (days < 1460) return 'Very Old';
    return 'Stale';
}; 